import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'

export default function useMongoDBFunctions() {
  const { getItemsAggregate } = useLambda()

  const eventsForSupervisedWorkers = async ({ offset, limit, userId, workers, locations, physical_locations, roles, date_gte, date_lt, privileges, client_id }) => {
    let events = []

    let filter = {
      deleted: { $ne: true },
      client_id: oid(client_id)
    }

    if (['supervisor', 'director'].includes(privileges)){
      filter.supervisors = oid(userId)
    }

    if (workers && workers.length) {
      filter._id = {
        "$in": workers.map(worker => oid(worker))
      }
    }

    if (locations && locations.length) {
      filter.locations = {
        "$in": locations.map(location => oid(location))
      }
    }

    if (physical_locations && physical_locations.length) {
      filter.physical_locations = {
        "$in": physical_locations.map(pl => oid(pl))
      }
    }

    if (roles && roles.length) {
      filter.roles = {
        "$in": roles.map(role => oid(role))
      }
    }

    const pipeline = [
      {
        $match: filter
      },
      {
        $lookup: {
          from: "location",
          localField: "locations",
          foreignField: "_id",
          pipeline: [ { $project: { _id: 0, location: 1 } } ],
          as: "locations"
        }
      },
      {
        $lookup: {
          from: "physical_location",
          localField: "physical_locations",
          foreignField: "_id",
          pipeline: [ { $project: { _id: 0, name: 1 } } ],
          as: "physical_locations",
        }
      },
      {
        $lookup:
          {
            from: "event",
            localField: "_id",
            foreignField: "attendee",
            as: "events",
            pipeline: [
              {
                $match: {
                  deleted: {
                    $ne: true,
                  },
                  isBehaviourEvaluation: {
                    $ne: true,
                  },
                  isInstance: {
                    $ne: true,
                  },
                  $and: [
                    {
                      start: {
                        $gte: date(date_gte)
                      },
                    },
                    {
                      start: {
                        $lt: date(date_lt)
                      },
                    },
                  ],
                },
              },
              {
                $lookup: {
                  from: "confirmation",
                  localField: "confirmation",
                  foreignField: "_id",
                  as: "confirmationData",
                  pipeline: [
                    {
                      $lookup: {
                        from: "process",
                        localField: "process",
                        foreignField: "_id",
                        as: "processName",
                        pipeline: [
                          {
                            $project: {
                              _id: 0,
                              name: 1
                            }
                          }
                        ]
                      }
                    },
                    {
                      $lookup: {
                        from: "worker",
                        localField: "supervisor",
                        foreignField: "_id",
                        as: "supervisorName",
                        pipeline: [
                          { $project: { _id: 0, name: 1, locations: 1, physical_locations: 1 } },
                          {
                            $lookup: {
                              from: "location",
                              localField: "locations",
                              foreignField: "_id",
                              as: "locations",
                              pipeline: [{ $project: { _id: 0, location: 1 } }]
                            }
                          },
                          {
                            $lookup: {
                              from: "physical_location",
                              localField: "physical_locations",
                              foreignField: "_id",
                              as: "physical_locations",
                              pipeline: [{ $project: { _id: 0, name: 1 } }]
                            }
                          },                        
                        ]
                      }
                    },
                    {
                      $project: {
                        _id: { $toString: "$_id" },
                        date: 1,
                        supervisorName: { $arrayElemAt: ["$supervisorName.name", 0]},
                        processName: { $arrayElemAt: ["$processName.name", 0]},
                        supervisorLocations: { $arrayElemAt: ["$supervisorName.locations.location", 0] },
                        supervisorPhysical_locations: { $arrayElemAt: ["$supervisorName.physical_locations.name", 0] },
                      }
                    }
                  ]
                }
              },
              {
                $project: {
                  _id: 0,
                  extendedProps: 1,
                  confirmationData: { $arrayElemAt: ["$confirmationData", 0] }
                },
              },
            ],
          },
      },
      {
        $project: {
          _id: { $toString: "$_id" },
          name: 1,
          events: 1,
          locations: 1,
          physical_locations: 1,
          numEventsDone: {
            $size: {      
              $filter: {
                input: "$events",
                as: "event",
                cond: {
                  $eq: [
                    "$$event.extendedProps.calendar",
                    "Realizadas",
                  ],
                },
              },
            },
            },
          numEventsPending: {
            $size: {
              $filter: {
                input: "$events",
                as: "event",
                cond: {
                  $ne: [
                    "$$event.extendedProps.calendar",
                    "Realizadas",
                  ],
                },
              },
              },
            },
          numEventsDoneCero: {
            $cond: {
              if: {
                $gt: [
                  { $size: { $filter: { input: "$events", as: "event", cond: { $eq: ["$$event.extendedProps.calendar", "Realizadas"] } } } },
                  0
                ]
              },
              then: 0,
              else: 1,
            },
          },
            numEmployeesWithEvents: {
            $cond: {
              if: {
                $gt: [
                  { $size: { $filter: { input: "$events", as: "event", cond: { $eq: ["$$event.extendedProps.calendar", "Realizadas"] } } } },
                  0
                ]
              },
              then: 1,
              else: 0,
            },
          },
        },
      },
      {
        $sort: {
          "numEventsDone": 1,
          "name": 1
        }
      },
      {
        $group: {
          _id: null,
          totalNumEventsDoneCero: { $sum: "$numEventsDoneCero" },
          totalNumEmployeesWithEvents: { $sum: "$numEmployeesWithEvents" },
          totalNumEventsDone: { $sum: "$numEventsDone"},
          eventsData: { $push: { _id: "$_id", name: "$name", events: "$events", physical_locations: "$physical_locations", locations: "$locations", numEventsDone: "$numEventsDone", numEventsPending: "$numEventsPending" } }, // Agrupamos los documentos en el arreglo "eventsData"
        }
      },
      {
        $project: {
          _id: 0, // Eliminamos el _id del objeto generado por la etapa $group
          eventsData: {
            $slice: ["$eventsData", offset || 0, limit || 10]
          },
          totalNumEventsDoneCero: 1,
          totalNumEmployeesWithEvents: 1,
          totalNumEventsDone: 1,
          totalEmployees: {
            $size: "$eventsData"
          }
        }
      },
    ]

    // if (search) {
    //   pipeline.push(
    //     {
    //       $match: {
    //         $or: [
    //           {
    //             name: {
    //               $regex: search,
    //               $options: "i",
    //             },
    //           },
    //           {
    //             code: {
    //               $regex: search,
    //               $options: "i",
    //             },
    //           },
    //         ],
    //       },
    //     }
    //   )
    // }

    // let pipelineCount = [
    //   ...pipeline,
    //   {
    //     $count: "count"
    //   }
    // ]

    // pipeline.push(
    //   {
    //     $skip: offset || 0
    //   },
    //   {
    //     $limit: limit || 10
    //   }
    // )

    events = await getItemsAggregate({ collection: 'worker', pipeline })

    return {
      events
    }
  
  };

  const commitmentReport = async ({ clientId, privileges, userId, locations, roles, processes, workers, date_gte, date_lt }) => {
    const workerFilter = {
      client_id: oid(clientId),
      deleted: { $ne: true },
    };

    if (['supervisor', 'director'].includes(privileges)) workerFilter.supervisors = oid(userId);

    if (locations && locations.length) workerFilter.locations = { "$in": locations.map(location => oid(location)) };

    if (roles && roles.length) workerFilter.roles = { "$in": roles.map(role => oid(role)) };

    if (workers && workers.length) workerFilter._id = { "$in": workers.map(worker => oid(worker)) };

    const confirmationFilter = {
      pending: { $ne: true },
      $and: [ { date: { $gte: date(date_gte) } }, { date: { $lt: date(date_lt) } } ]
    };

    if (processes && processes.length) confirmationFilter.process = { "$in": processes.map(process => oid(process)) };

    const pipeline = [
      {
        $match: workerFilter
      },
      {
        $project: {
          name: 1,
        },
      },
      {
        $lookup: {
          from: "confirmation",
          localField: "_id",
          foreignField: "worker",
          pipeline: [
            {
              $match: confirmationFilter
            },
            {
              $sort: {
                date: -1,
                _id: -1
              },
            },
            {
              $group: {
                _id: "$process",
                confirmations: {
                  $push: {
                    confirmationId: "$_id",
                    activities: "$activities",
                    score: "$score",
                    total: "$total",
                    date: "$date",
                  },
                },
              },
            },
            {
              $project: {
                _id: 0,
                processId: "$_id",
                lastConfirmation: {
                  $arrayElemAt: ["$confirmations", 0],
                },
              },
            },
            {
              $sort: {
                processId: 1
              }
            },
            {
              $lookup: {
                from: "process",
                localField: "processId",
                foreignField: "_id",
                pipeline: [
                  {
                    $project: {
                      _id: 0,
                      name: 1,
                    },
                  },
                ],
                as: "processName",
              },
            },
            {
              $addFields: {
                processName: {
                  $arrayElemAt: ["$processName.name", 0],
                },
              },
            },
            {
              $lookup: {
                from: "commitment",
                localField: "lastConfirmation.confirmationId",
                foreignField: "confirmation",
                pipeline: [
                  {
                    $project: {
                      _id: 1,
                      behaviour: 1,
                      completed: 1,
                      activity: 1,
                      dueDate: 1,
                      creationDate: 1,
                      resolutionDate: 1
                    },
                  },
                  {
                    $sort: {
                      _id: -1
                    },
                  }
                ],
                as: "commitments",
              },
            },
          ],
          as: "confirmationData",
        },
      },
      {
        $addFields: {
          totalScore: {
            $sum: "$confirmationData.lastConfirmation.score",
          },
          totalTotal: {
            $sum: "$confirmationData.lastConfirmation.total",
          },
        },
      },
      {
        $addFields: {
          averageAdherence: {
            $cond: [
              {
                $ne: ["$totalTotal", 0],
              },
              {
                $divide: ["$totalScore", "$totalTotal"],
              },
              null,
            ],
          },
        },
      },
      {
        $sort: {
          averageAdherence: 1,
        },
      }
    ];

    const commitments = await getItemsAggregate({ collection: 'worker', pipeline })


    return {
      commitments,
    };
  };

  const confirmationsForWorkers = async ({ offset, limit, userId, processes, supervisors, locations, roles, privileges, client_id, date_gte, date_lt}) => {
    let workersWithPagination = []
    let pipeline = []
    let workers = []

    let filterWorker = {
      deleted: { $ne: true },
      client_id: oid(client_id),
      privileges: { $in: [ "supervisor", "director", "consultant" ] }
    }

    if (locations && locations.length) {
      filterWorker.locations = {
        "$in": locations.map(location => oid(location))
      }
    }

    if (roles && roles.length) {
      filterWorker.roles = {
        "$in": roles.map(role => oid(role))
      }
    }

    if (['supervisor', 'director'].includes(privileges)){
      filterWorker.$or = [
        { supervisors : oid(userId)},
        { _id : oid(userId)}
      ]
    }

    if (supervisors && supervisors.length) {
      filterWorker._id = {
        "$in": supervisors.map(supervisor => oid(supervisor))
      }
    }

    let filterConfirmation = {
      date: {
        $gte: date(date_gte),
        $lt: date(date_lt),
      }
    }

    if (processes && processes.length) {
      filterConfirmation.process = {
        "$in": processes.map(process => oid(process))
      }
    }

    pipeline = [
      {
        $match: filterWorker
      },
      {
        $lookup:
          {
            from: "confirmation",
            localField: "_id",
            foreignField: "supervisor",
            as: "confirmations",
            pipeline: [
              {
                $match: filterConfirmation,
              },
              {
                $project: {
                  _id: 1,
                },
              },
            ],
          },
      },
      {
        $project: {
          name: 1,
          roles: 1,
          locations: 1,
          confirmationsCount: {
            $size: "$confirmations"
          }
        },
      },
      {
        $sort: {
          confirmationsCount: -1,
          name: 1
        }
      },
      {
        $lookup:
          {
            from: "location",
            localField: "locations",
            foreignField: "_id",
            as: "locationsData",
            pipeline: [
              {
                $match: {
                  deleted: {
                    $ne: true,
                  },
                },
              },
              {
                $project: {
                  location: 1,
                  physical_location: 1,
                },
              },
              {
                $lookup:
                  {
                    from: "physical_location",
                    localField: "physical_location",
                    foreignField: "_id",
                    as: "physical_location",
                    pipeline: [
                      {
                        $project: {
                          name: 1,
                        },
                      },
                    ],
                  },
              },
              {
                $addFields: {
                  physical_location: { $arrayElemAt: ["$physical_location.name", 0] },
                },
              },
            ],
          },
      },
      {
        $lookup:
          {
            from: "role",
            localField: "roles",
            foreignField: "_id",
            as: "rolesData",
            pipeline: [
              {
                $match: {
                  deleted: {
                    $ne: true,
                  },
                },
              },
              {
                $project: {
                  _id: 0,
                  numEventsRequired: 1,
                },
              },
            ],
          },
      },
      {
        $project: {
          _id: 1,
          name: 1,
          confirmationsCount: 1,
          rolesData: 1,
          locationsData: 1,
        },
      },
    ]

    workers = await getItemsAggregate({ collection: 'worker', pipeline })

    pipeline.push(
      {
        $skip: offset || 0
      },
      {
        $limit: limit || 10
      }
    )

    workersWithPagination = await getItemsAggregate({ collection: 'worker', pipeline })

    return {
      workersWithPagination,
      workers
    }
    
  };

  const processesQueryWithOffset = async ({ query, offset, limit, total, search, sortBy, sortOrder, client_id = '', role, worker_id, locations, locationFilter, physicalLocationFilter }) => {
    let processes = []
    let totalRecords = 0
    let pipeline = []
    let collection = ''
    const locationFilterIds = locationFilter?.map(id => oid(id)) || []
    const physicalLocationFilterIds = physicalLocationFilter?.map(id => oid(id)) || []

    // Fetch only the processes related to the supervisor and workers supervised roles
    if (["supervisor", "director"].includes(role)) {
      collection = "worker";
      const supervisorID = oid(worker_id)
      const locationIds = locations?.map(id => oid(id)) || []

      pipeline = [
        {
          $match:
            {
              $or: [
                {
                  supervisors: supervisorID,
                  deleted: { $ne: true },
                },
                {
                  _id: supervisorID,
                },
              ],
            },
        },
        {
          $unwind:
            {
              path: "$roles",
            },
        },
        {
          $group:
            {
              _id: "$roles",
            },
        },
        {
          $lookup:
            {
              from: "confirmation_planning",
              localField: "_id",
              foreignField: "role",
              as: "processes",
              pipeline: [
                {
                  $match: {
                    deleted: {
                      $ne: true,
                    },
                  },
                },
                {
                  $project: {
                    _id: 0,
                    process: 1,
                  },
                },
              ],
            },
        },
        {
          $unwind:
            {
              path: "$processes",
            },
        },
        {
          $group:
            {
              _id: "$processes.process",
            },
        },
        {
          $lookup: {
            from: "process",
            let: {
              processId: "$_id",
              supervisorLocations: locationIds
            },
            as: "processData",
            pipeline: [
              {
                $match: {
                  $expr: {
                    $and: [
                      { $eq: ["$_id", "$$processId"] },
                      { $ne: ["$deleted", true] },
                      {
                        $or: [
                          // No tiene locations
                          {
                            $eq: [
                              { $ifNull: ["$locations", []] },
                              []
                            ]
                          },
                          // Tiene intersección con las del supervisor
                          {
                            $gt: [
                              {
                                $size: {
                                  $setIntersection: [
                                    { $ifNull: ["$locations", []] },
                                    "$$supervisorLocations"
                                  ]
                                }
                              },
                              0
                            ]
                          }
                        ]
                      }
                    ]
                  }
                }
              },
            ]
          }
        },
        {
          $unwind:
            {
              path: "$processData",
            },
        },
        {
          $replaceRoot:
            {
              newRoot: "$processData",
            },
        },

        // -----------------------
        // UNION con procesos fuera de planning pero con misma location
        // -----------------------
        {
          $unionWith: {
            coll: "process",
            pipeline: [
              {
                $match: {
                  deleted: { $ne: true }
                }
              },
              {
                $match: {
                  $expr: {
                    $gt: [
                      {
                        $size: {
                          $setIntersection: [
                            { $ifNull: ["$locations", []] },
                            locationIds
                          ]
                        }
                      },
                      0
                    ]
                  }
                }
              }
            ]
          }
        },

        // -----------------------
        // Eliminar duplicados
        // -----------------------
        {
          $group: {
            _id: "$_id",
            doc: { $first: "$$ROOT" }
          }
        },
        {
          $replaceRoot: { newRoot: "$doc" }
        },
        {
          $lookup: {
            from: "location",
            localField: "locations",
            foreignField: "_id",
            pipeline: [
              {
                $lookup: {
                  from: "physical_location",
                  localField: "physical_location",
                  foreignField: "_id",
                  as: "physicalLocationData"
                }
              },
              {
                $unwind: {
                  path: "$physicalLocationData",
                  preserveNullAndEmptyArrays: true
                }
              },
              {
                $project: {
                  location: 1,
                  physicalLocationName: "$physicalLocationData.name"
                }
              }
            ],
            as: "locationsData"
          }
        },
        {
          $addFields: {
            locationsWithPhysicalLocations: {
              $map: {
                input: { $ifNull: ["$locationsData", []] },
                as: "loc",
                in: {
                  $cond: [
                    { $ifNull: ["$$loc.physicalLocationName", false] },
                    {
                      $concat: [
                        "$$loc.location",
                        " - ",
                        "$$loc.physicalLocationName"
                      ]
                    },
                    "$$loc.location"
                  ]
                }
              }
            }
          }
        }
      ]
    } else {
      collection = "process";
      
      pipeline = [
        {
          $match:
            {
              deleted: { $ne: true },
              client_id: oid(client_id)
            },
        },
        {
          $lookup: {
            from: "location",
            localField: "locations",
            foreignField: "_id",
            pipeline: [
              {
                $lookup: {
                  from: "physical_location",
                  localField: "physical_location",
                  foreignField: "_id",
                  as: "physicalLocationData"
                }
              },
              {
                $unwind: {
                  path: "$physicalLocationData",
                  preserveNullAndEmptyArrays: true
                }
              },
              {
                $project: {
                  location: 1,
                  physicalLocationName: "$physicalLocationData.name",
                  physical_location: 1
                }
              }
            ],
            as: "locationsData"
          }
        },
        {
          $addFields: {
            locationsWithPhysicalLocations: {
              $map: {
                input: { $ifNull: ["$locationsData", []] },
                as: "loc",
                in: {
                  $cond: [
                    { $ifNull: ["$$loc.physicalLocationName", false] },
                    {
                      $concat: [
                        "$$loc.location",
                        " - ",
                        "$$loc.physicalLocationName"
                      ]
                    },
                    "$$loc.location"
                  ]
                }
              }
            }
          }
        }
      ]

      const andConditions = []

      if (locationFilterIds.length) {
        andConditions.push({
          $gt: [
            {
              $size: {
                $setIntersection: [
                  { $ifNull: ["$locations", []] },
                  locationFilterIds
                ]
              }
            },
            0
          ]
        })
      }

      if (physicalLocationFilterIds.length) {
        andConditions.push({
          $gt: [
            {
              $size: {
                $setIntersection: [
                  {
                    $filter: {
                      input: {
                        $map: {
                          input: {
                            $cond: [
                              { $isArray: "$locationsData" },
                              "$locationsData",
                              []
                            ]
                          },
                          as: "loc",
                          in: "$$loc.physical_location"
                        }
                      },
                      as: "pl",
                      cond: { $ne: ["$$pl", null] }
                    }
                  },
                  physicalLocationFilterIds
                ]
              }
            },
            0
          ]
        })
      }

      pipeline.push({
        $match: {
          $expr: {
            $and: andConditions
          }
        }
      })
    }

    pipeline.push(
      {
        $lookup: {
          from: "metadata",
          localField: "metadata",
          foreignField: "_id",
          as: "metadataData",
          pipeline: [
            {
              $match: {
                deleted: {
                  $ne: true,
                }
              }
            },
            {
              $project: {
                name: 1,
                type: 1,
              }
            }
          ]
        }
      }
    )

    if (search) {
      pipeline.push(
        {
          $match: {
            $or: [
              {
                name: {
                  $regex: search,
                  $options: "i",
                },
              },
              {
                code: {
                  $regex: search,
                  $options: "i",
                },
              },
            ],
          },
        }
      )
    }

    let pipelineCount = [
      ...pipeline,
      {
        $count: "count"
      }
    ]

    pipeline.push(
      {
        $sort: {
          [sortBy]: sortOrder || 1,
          _id: sortOrder || 1
        }
      },
      {
        $skip: offset || 0
      },
      {
        $limit: limit || 10
      }
    )

    processes = await getItemsAggregate({ collection, pipeline })
    if (total) {
      const totalArray = await getItemsAggregate({ collection, pipeline: pipelineCount });
      totalRecords = totalArray.length ? totalArray[0].count : 0;
    }
    
    return {
      processes,
      totalRecords
    }
    
  };

  const confirmationPlanningGroupByRole = async ({ client_id = '' }) => {
    let pipeline = [
      {
        $match: {
          deleted: { $ne: true }
        }
      },
      {
        $sort: {
          confirmationsPerYear: -1
        }
      },
      {
        $group: {
          _id: '$role',
          planning: {
            $push: {
              process: '$process',
              confirmationsPerYear: '$confirmationsPerYear',
              locations: '$locations'
            }
          },
          confirmationsTotal: {
            $sum: '$confirmationsPerYear'
          }
        }
      }
    ]

    if (client_id) pipeline[0].$match.client_id = oid(client_id)

    const rolesData = await getItemsAggregate({ collection: 'confirmation_planning', pipeline })

    return {
      rolesData,
    }

  };

  const confirmationPlanningQueryWithOffset = async ({ offset, limit, total, search, sortBy, sortOrder, client_id = '', processes, roles, locationFilter, physicalLocationFilter }) => {
    let totalRecordsCount = []
    let totalRecords = 0
    const locationFilterIds = locationFilter?.map(id => oid(id)) || []
    const physicalLocationFilterIds = physicalLocationFilter?.map(id => oid(id)) || []
    
    let filter = {
      deleted: { $ne: true }
    }

    if (client_id) {
      filter.client_id = oid(client_id)
    }

    if (processes && processes.length) {
      filter.process = {
        "$in": processes.map(process => oid(process))
      }
    }

    if (roles && roles.length) {
      filter.role = {
        "$in": roles.map(role => oid(role))
      }
    }

    let pipeline = [
      {
        $match: filter
      },
      {
        $lookup: {
          from: 'role',
          localField: 'role',
          foreignField: '_id',
          pipeline: [
            {
              $project: {
                name: 1
              }
            }
          ],
          as: 'roleName'
        }
      },
      {
        $lookup: {
          from: 'process',
          localField: 'process',
          foreignField: '_id',
          pipeline: [
            {
              $project: {
                name: 1,
                locations: 1
              }
            }
          ],
          as: 'processName'
        }
      },
      {
        $lookup: {
          from: "location",
          localField: "locations",
          foreignField: "_id",
          pipeline: [
            {
              $lookup: {
                from: "physical_location",
                localField: "physical_location",
                foreignField: "_id",
                as: "physicalLocationData"
              }
            },
            {
              $unwind: {
                path: "$physicalLocationData",
                preserveNullAndEmptyArrays: true
              }
            },
            {
              $project: {
                location: 1,
                physicalLocationName: "$physicalLocationData.name",
                physical_location: 1
              }
            }
          ],
          as: "locationsData"
        }
      },
      {
        $addFields: {
          locationsWithPhysicalLocations: {
            $map: {
              input: { $ifNull: ["$locationsData", []] },
              as: "loc",
              in: {
                $cond: [
                  { $ifNull: ["$$loc.physicalLocationName", false] },
                  {
                    $concat: [
                      "$$loc.location",
                      " - ",
                      "$$loc.physicalLocationName"
                    ]
                  },
                  "$$loc.location"
                ]
              }
            }
          }
        }
      }
    ]

    const andConditions = []

    if (locationFilterIds.length) {
      andConditions.push({
        $gt: [
          {
            $size: {
              $setIntersection: [
                {
                  $ifNull: ["$locations", []]
                },
                locationFilterIds
              ]
            }
          },
          0
        ]
      })
    }

    if (physicalLocationFilterIds.length) {
      andConditions.push({
        $gt: [
          {
            $size: {
              $setIntersection: [
                {
                  $filter: {
                    input: {
                      $map: {
                        input: {
                          $cond: [
                            { $isArray: "$locationsData" },
                            "$locationsData",
                            []
                          ]
                        },
                        as: "loc",
                        in: "$$loc.physical_location"
                      }
                    },
                    as: "pl",
                    cond: { $ne: ["$$pl", null] }
                  }
                },
                physicalLocationFilterIds
              ]
            }
          },
          0
        ]
      })
    }

    pipeline.push({
      $match: {
        $expr: {
          $and: andConditions
        }
      }
    })

    if (search) {
      pipeline.push(
        {
          $match: {
            $or: [
              {
                roleName: {
                  $elemMatch: {
                    name: {
                      $regex: search,
                      $options: 'i'
                    }
                  }
                }
              },
              {
                processName: {
                  $elemMatch: {
                    name: {
                      $regex: search,
                      $options: 'i'
                    }
                  }
                }
              }
            ]
          }
        }
      )
    }

    totalRecordsCount = await getItemsAggregate({ collection: 'confirmation_planning', pipeline })

    totalRecords = totalRecordsCount.length;

    pipeline.push(
      {
        $sort: {
          [sortBy]: sortOrder || 1,
          _id: sortOrder || 1
        }
      },
      {
        $skip: offset || 0
      },
      {
        $limit: limit || 10
      }
    )
    
    const confirmationPlannings = await getItemsAggregate({ collection: 'confirmation_planning', pipeline })

    return {
      confirmationPlannings,
      totalRecords
    }
  };

  const documentsQueryWithOffset = async ({ query, offset, limit, total, search, sortBy, sortOrder, client_id = null }) => {
    let condition = query || {};
    condition.deleted = { $ne: true };

    if (search) {
      const searchConditions = [
        { name: { $regex: search, $options: 'i' } }
      ];

      if (client_id) {
        condition = {
          ...condition,
          client_id: oid(client_id),
          $or: searchConditions
        };
      } else {
        condition = {
          ...condition,
          $or: searchConditions
        };
      }

    } else if (client_id) {
      condition = {
        ...condition,
        client_id: oid(client_id)
      };
    }

    const pipeline = [
      { $match: condition },
      {
        $facet: {
          documents: [
            { $sort: { [sortBy]: sortOrder !== undefined ? sortOrder : 1 } },
            { $skip: offset || 0 },
            { $limit: limit || 10 },
            {
              $project: {
                _id: 1,
                name: 1,
                key: 1
              }
            }
          ],
          totalRecords: total ? [
            { $count: "count" }
          ] : []
        }
      }
    ];

    const result = await getItemsAggregate({ collection: 'document', pipeline })

    return {
      documents: result[0].documents,
      totalRecords: total ? (result[0].totalRecords[0]?.count || 0) : 0
    };
  };

  const pendingConfirmationsQueryWithOffset = async ({ offset, limit, total, search, sortBy, sortOrder, worker_id = '' }) => {
    let totalRecords = 0;
    let totalRecordsCount = []

    
    let pipeline = [
      {
        $match: {
          pending: true,
          supervisor: oid(worker_id),
          deleted: { $ne: true }
        }
      },
      {
        $lookup: {
          from: 'worker',
          localField: 'worker',
          foreignField: '_id',
          pipeline: [
            {
              $project: {
                name: 1
              }
            }
          ],
          as: 'workerName'
        }
      },
      {
        $lookup: {
          from: 'process',
          localField: 'process',
          foreignField: '_id',
          pipeline: [
            {
              $project: {
                name: 1
              }
            }
          ],
          as: 'processName'
        }
      },
      {
        $project: {
          date: 1,
          workerName: 1,
          processName: 1
        }
      },
    ]

    if (search) {
      pipeline.push(
        {
          $match: {
            $or: [
              {
                workerName: {
                  $elemMatch: {
                    name: {
                      $regex: search,
                      $options: 'i'
                    }
                  }
                }
              },
              {
                processName: {
                  $elemMatch: {
                    name: {
                      $regex: search,
                      $options: 'i'
                    }
                  }
                }
              }
            ]
          }
        }
      )
    }

    totalRecordsCount = await getItemsAggregate({ collection: 'confirmation', pipeline });

    totalRecords = totalRecordsCount.length;

    pipeline.push(
      {
        $sort: {
          [sortBy]: sortOrder || 1,
          _id: sortOrder || 1
        }
      },
      {
        $skip: offset || 0
      },
      {
        $limit: limit || 10
      }
    )
    
    const pendingConfirmations = await getItemsAggregate({ collection: 'confirmation', pipeline })

    return {
      pendingConfirmations,
      totalRecords
    }
    
  };

  const rolesQueryWithOffset = async ({ query, offset, limit, total, search, sortBy, sortOrder, client_id = '' }) => {
    let condition = query || {};
    condition.deleted = { $ne: true };

    if (search) {
      const searchConditions = [
        { name: { $regex: search, $options: 'i' } },
        { code: { $regex: search, $options: 'i' } },
        { description: { $regex: search, $options: 'i' } }
      ];

      if (client_id !== '') {
        condition = {
          ...condition,
          client_id: oid(client_id),
          $or: searchConditions
        };
      } else {
        condition = {
          ...condition,
          $or: searchConditions
        };
      }

    } else if (client_id !== '') {
      condition = {
        ...condition,
        client_id: oid(client_id)
      };
    }

    const pipeline = [
      { $match: condition },
      {
        $facet: {
          roles: [
            { $sort: { [sortBy]: sortOrder !== undefined ? sortOrder : 1 } },
            { $skip: offset || 0 },
            { $limit: limit || 10 },
            {
              $project: {
                _id: 1,
                name: 1,
                code: 1,
                description: 1,
                responsibilities: 1,
                qualifications: 1
              }
            }
          ],
          totalRecords: total ? [
            { $count: "count" }
          ] : []
        }
      }
    ];

    const result = await getItemsAggregate({ collection: 'role', pipeline })

    return {
      roles: result[0].roles,
      totalRecords: total ? (result[0].totalRecords[0]?.count || 0) : 0
    };
  };

  const workersQueryWithOffset = async ({ query, offset, limit, total, search, sortBy, sortOrder, supervisor, client_id = '', location, physical_location, roles }) => {
    let condition = query || {};
    condition.deleted = { $ne: true };

    if (supervisor)
      condition.supervisors = oid(supervisor);

    if (search) {
      const searchConditions = [
        { name: { $regex: search, $options: 'i' } },
        { email: { $regex: search, $options: 'i' } }
      ];

      if (client_id !== '') {
        condition = {
          ...condition,
          client_id: oid(client_id),
          $or: searchConditions
        };
      } else {
        condition = {
          ...condition,
          $or: searchConditions
        };
      }

    } else if (client_id !== '') {
      condition = {
        ...condition,
        client_id: oid(client_id)
      };
    }

    if (location && location.length) {
      condition.locations = {
        $in: location.map(l => oid(l))
      };
    }

    if (physical_location && physical_location.length) {
      condition.physical_locations = {
        $in: physical_location.map(pl => oid(pl))
      };
    }

    if (roles && roles.length) {
      condition.roles = {
        $in: roles.map(r => oid(r))
      };
    }

    const pipeline = [
      { $match: condition },
      {
        $facet: {
          workers: [
            { $sort: { [sortBy]: sortOrder !== undefined ? sortOrder : 1 } },
            { $skip: offset || 0 },
            { $limit: limit || 10 },
            {
              $project: {
                _id: 1,
                name: 1,
                email: 1,
                roles: 1,
                supervisors: 1,
                privileges: 1,
                locations: 1,
                physical_locations: 1
              }
            }
          ],
          workersTotal: [
            {
              $lookup: {
                from: "location",
                localField: "locations",
                foreignField: "_id",
                as: "locationsData",
                pipeline: [
                  { $project: { _id: 0, location: 1 } }
                ]
              }
            },
            {
              $lookup: {
                from: "physical_location",
                localField: "physical_locations",
                foreignField: "_id",
                as: "physicalLocationsData",
                pipeline: [
                  { $project: { _id: 0, name: 1 } }
                ]
              }
            },
            {
              $lookup: {
                from: "role",
                localField: "roles",
                foreignField: "_id",
                as: "rolesData",
                pipeline: [
                  { $project: { _id: 0, name: 1 } }
                ]
              }
            },
            {
              $lookup: {
                from: "worker",
                localField: "supervisors",
                foreignField: "_id",
                as: "supervisorsData",
                pipeline: [
                  { $project: { _id: 0, name: 1 } }
                ]
              }
            },
            {
              $project: {
                _id: 0,
                email: 1,
                name: 1,
                privileges: 1,
                locationsData: 1,
                physicalLocationsData: 1,
                rolesData: 1,
                supervisorsData: 1
              }
            }
          ],
          totalRecords: total ? [
            { $count: "count" }
          ] : []
        }
      }
    ];

    const result = await getItemsAggregate({ collection: 'worker', pipeline })

    return {
      workers: result[0].workers,
      workersTotal: result[0].workersTotal,
      totalRecords: total ? (result[0].totalRecords[0]?.count || 0) : 0
    };
  };
  
  const supervisorsCountByLocation = async ({ client_id = '' }) => {
    let pipeline = [
      {
        $match: {
          deleted: { $ne: true },
          privileges: { $in: ['supervisor', 'director'] }
        }
      },
      {
        $unwind: '$locations'
      },
      {
        $group: {
          _id: '$locations',
          count: {
            $sum: 1
          }
        }
      },
      {
        $project: {
          _id: { $toString: '$_id' },
          count: 1
        }
      }
    ]

    if (client_id) pipeline[0].$match.client_id = oid(client_id)

    const result = await getItemsAggregate({ collection: 'worker', pipeline })

    return {
      supervisorsByLocation: result
    }
  };

  const workersCountByRole = async ({ client_id = '' }) => {
    let pipeline = [
      {
        $match: {
          deleted: { $ne: true }
        }
      },
      {
        $unwind: '$roles'
      },
      {
        $unwind: {
          path: '$locations',
          preserveNullAndEmptyArrays: true
        }
      },
      {
        $group: {
          _id: {
            location: '$locations',
            role: '$roles'
          },
          count: {
            $sum: 1
          }
        }
      },
      {
        $project: {
          _id: 0,
          location: { $ifNull: [{ $toString: '$_id.location' }, 'no_location'] },
          role: { $toString: '$_id.role' },
          count: 1
        }
      }
    ]

    if (client_id) pipeline[0].$match.client_id = oid(client_id)

    const result = await getItemsAggregate({ collection: 'worker', pipeline })

    return {
      workersByRole: result
    }
  };

  return {
    eventsForSupervisedWorkers,
    commitmentReport,
    confirmationsForWorkers,
    processesQueryWithOffset,
    confirmationPlanningGroupByRole,
    confirmationPlanningQueryWithOffset,
    documentsQueryWithOffset,
    pendingConfirmationsQueryWithOffset,
    rolesQueryWithOffset,
    workersQueryWithOffset,
    supervisorsCountByLocation,
    workersCountByRole,
  }
}